// src/components/Navbar.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import supabase from "../supabaseClient";
import { useNavigationState } from "../context/NavigationContext";
import "../estilos/EstilosGlobales.css";
import { useTenant } from "../context/TenantContext";
import { limpiarCacheTenant } from "../utils/tenantPDF";

const Navbar = ({ onMenuClick }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [usuario] = useState(() => { try { return JSON.parse(localStorage.getItem("usuario")); } catch { return null; } });
  const esLogin = location.pathname === "/" || location.pathname === "/login";
  const { clearAllStates } = useNavigationState();
  const { tenant } = useTenant();

  // ── Notificaciones ──
  const [notificaciones, setNotificaciones] = useState([]);
  const [mostrarPanel, setMostrarPanel] = useState(false);
  const [leidas, setLeidas] = useState(() => {
    try { return JSON.parse(localStorage.getItem("sw_notif_leidas") || "[]"); } catch { return []; }
  });
  const panelRef = useRef(null);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    localStorage.removeItem("usuario");
    localStorage.removeItem("sesion");
    limpiarCacheTenant();
    clearAllStates();
    navigate("/");
  };

  const handleHamburgerClick = () => {
    if (onMenuClick) onMenuClick();
  };

  // ── Cargar notificaciones (citas de hoy y mañana) ──
  const cargarNotificaciones = useCallback(async () => {
    if (!usuario || esLogin) return;
    try {
      const fechaLocal = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
      const hoy = new Date();
      const manana = new Date(hoy);
      manana.setDate(manana.getDate() + 1);
      const hoyStr = fechaLocal(hoy);
      const mananaStr = fechaLocal(manana);

      const { data: citasData } = await supabase
        .from("citas")
        .select("id, fecha, hora, motivo, estado, clientes(nombre, telefono)")
        .in("fecha", [hoyStr, mananaStr])
        .in("estado", ["programada", "confirmada"])
        .order("fecha", { ascending: true })
        .order("hora", { ascending: true });

      if (!citasData) return;

      const items = citasData.map((c) => {
        const esHoy = c.fecha === hoyStr;
        return {
          id: c.id,
          tipo: "cita",
          titulo: `${esHoy ? "Hoy" : "Mañana"} ${(c.hora || "").slice(0, 5)} — ${c.clientes?.nombre || "Cliente"}`,
          detalle: c.motivo || "Cita agendada",
          fecha: c.fecha,
          emoji: esHoy ? "🔔" : "📅",
          color: esHoy ? "#f59e0b" : "#8b5cf6",
          telefono: c.clientes?.telefono || null,
        };
      });

      // 🆕 ── Alertas CRM inteligentes ──
      const hace5Dias = new Date(hoy);
      hace5Dias.setDate(hace5Dias.getDate() - 5);
      const hace5DiasStr = fechaLocal(hace5Dias);

      // 1) Cotizaciones pendientes sin respuesta (>5 días)
      const { count: cotPendientes } = await supabase
        .from("cotizaciones")
        .select("id", { count: "exact", head: true })
        .eq("estado", "pendiente")
        .lt("fecha", hace5DiasStr);

      if (cotPendientes > 0) {
        items.push({
          id: "alerta-cot-pendientes",
          tipo: "alerta",
          titulo: `${cotPendientes} cotización${cotPendientes > 1 ? "es" : ""} sin respuesta`,
          detalle: "Llevan más de 5 días sin convertirse en pedido",
          emoji: "📋",
          color: "#f59e0b",
        });
      }

      // 2) Pedidos con saldo pendiente de cobro
      const { count: pedidosPendientes } = await supabase
        .from("ordenes_pedido")
        .select("id", { count: "exact", head: true })
        .eq("estado", "pendiente")
        .eq("cerrada", false);

      if (pedidosPendientes > 0) {
        items.push({
          id: "alerta-pedidos-pago",
          tipo: "alerta",
          titulo: `${pedidosPendientes} pedido${pedidosPendientes > 1 ? "s" : ""} pendientes de cobro`,
          detalle: "Pedidos abiertos con saldo sin pagar",
          emoji: "💰",
          color: "#ef4444",
        });
      }

      // 3) Clientes dormidos (sin pedidos hace >270 días)
      const hace270Dias = new Date(hoy);
      hace270Dias.setDate(hace270Dias.getDate() - 270);
      const hace270Str = fechaLocal(hace270Dias);

      const { data: clientesConPedidos } = await supabase
        .from("ordenes_pedido")
        .select("cliente_id, fecha_evento")
        .not("cliente_id", "is", null);

      if (clientesConPedidos && clientesConPedidos.length > 0) {
        const ultimaFecha = {};
        clientesConPedidos.forEach((o) => {
          const f = o.fecha_evento || "";
          if (!ultimaFecha[o.cliente_id] || f > ultimaFecha[o.cliente_id]) {
            ultimaFecha[o.cliente_id] = f;
          }
        });
        const dormidos = Object.values(ultimaFecha).filter((f) => f < hace270Str).length;
        if (dormidos > 0) {
          items.push({
            id: "alerta-dormidos",
            tipo: "alerta",
            titulo: `${dormidos} cliente${dormidos > 1 ? "s" : ""} dormido${dormidos > 1 ? "s" : ""}`,
            detalle: "No alquilan hace más de 9 meses",
            emoji: "😴",
            color: "#6b7280",
          });
        }
      }

      setNotificaciones(items);
    } catch (err) {
      console.error("Error cargando notificaciones:", err);
    }
  }, [usuario, esLogin]);

  useEffect(() => {
    cargarNotificaciones();
    const intervalo = setInterval(cargarNotificaciones, 5 * 60 * 1000);
    return () => clearInterval(intervalo);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  // Cerrar panel al hacer click fuera
  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setMostrarPanel(false);
    };
    if (mostrarPanel) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [mostrarPanel]);

  // ── Marcar como leída y navegar ──
  const manejarClickNotif = (notif) => {
    const nuevasLeidas = [...new Set([...leidas, notif.id])];
    setLeidas(nuevasLeidas);
    localStorage.setItem("sw_notif_leidas", JSON.stringify(nuevasLeidas));
    setMostrarPanel(false);
    // 🆕 Navegar al módulo correcto según el tipo de alerta
    if (notif.id === "alerta-cot-pendientes") return navigate("/cotizaciones-guardadas");
    if (notif.id === "alerta-pedidos-pago") return navigate("/pagos-pendientes");
    if (notif.id === "alerta-dormidos") return navigate("/clientes");
    navigate("/agenda");
  };

  const marcarTodasLeidas = () => {
    const todas = notificaciones.map((n) => n.id);
    const nuevasLeidas = [...new Set([...leidas, ...todas])];
    setLeidas(nuevasLeidas);
    localStorage.setItem("sw_notif_leidas", JSON.stringify(nuevasLeidas));
  };

  const sinLeer = notificaciones.filter((n) => !leidas.includes(n.id)).length;

  return (
    <nav className="sw-navbar">
      <div className="sw-navbar-contenido">
        {/* Sección izquierda: Hamburguesa + Logo */}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {usuario && !esLogin && (
            <button
              onClick={handleHamburgerClick}
              style={{
                display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center",
                width: 36, height: 36, background: "rgba(255,255,255,0.1)", border: "none",
                borderRadius: 6, cursor: "pointer", padding: 8, gap: 4, transition: "background 0.2s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.2)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.1)")}
              title="Menú" aria-label="Abrir menú"
            >
              <span style={{ display: "block", width: 18, height: 2, background: "white", borderRadius: 1 }} />
              <span style={{ display: "block", width: 18, height: 2, background: "white", borderRadius: 1 }} />
              <span style={{ display: "block", width: 18, height: 2, background: "white", borderRadius: 1 }} />
            </button>
          )}
          <Link to={esLogin ? "/" : "/inicio"} className="sw-navbar-marca" style={{ textDecoration: "none" }}>
            {!esLogin && usuario && (
              <img src="/icons/swalquiler-logo.webp" alt="SwAlquiler" className="sw-navbar-logo" />
            )}
            <div>
              <h1 className="sw-navbar-titulo">SwAlquiler</h1>
              {!esLogin && <p className="sw-navbar-subtitulo">{tenant?.nombre || "Cargando..."}</p>}
            </div>
          </Link>
        </div>

        {/* Acciones */}
        {usuario && !esLogin && (
          <div className="sw-navbar-acciones">
            {/* Navegación rápida (solo PC) */}
            <button className="sw-navbar-btn-icon" onClick={() => navigate(-1)} title="Atrás"
              style={{ display: window.innerWidth > 768 ? "flex" : "none" }}>←</button>
            <button className="sw-navbar-btn-icon" onClick={() => navigate(1)} title="Adelante"
              style={{ display: window.innerWidth > 768 ? "flex" : "none" }}>→</button>
            <button className="sw-navbar-btn-icon" onClick={() => navigate("/inicio")} title="Inicio"
              style={{ display: window.innerWidth > 768 ? "flex" : "none" }}>🏠</button>

            {/* ═══ CAMPANA DE NOTIFICACIONES ═══ */}
            <div ref={panelRef} style={{ position: "relative" }}>
              <button
                className="sw-navbar-btn-icon"
                onClick={() => setMostrarPanel(!mostrarPanel)}
                title={sinLeer > 0 ? `${sinLeer} notificación(es) sin leer` : "Sin notificaciones nuevas"}
                style={{ position: "relative" }}
              >
                🔔
                {sinLeer > 0 && (
                  <span style={{
                    position: "absolute", top: -2, right: -2,
                    width: 18, height: 18, borderRadius: "50%",
                    background: "#ef4444", color: "white",
                    fontSize: 10, fontWeight: 700,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    border: "2px solid #0077B6",
                    animation: "notifPulse 2s infinite",
                  }}>
                    {sinLeer > 9 ? "9+" : sinLeer}
                  </span>
                )}
              </button>

              {/* ── Panel dropdown ── */}
              {mostrarPanel && (
                <div style={{
                  position: "absolute", top: "calc(100% + 8px)", right: 0,
                  width: 320, maxHeight: 400, overflowY: "auto",
                  background: "white", borderRadius: 12,
                  boxShadow: "0 12px 40px rgba(0,0,0,0.18)",
                  border: "1px solid #e5e7eb", zIndex: 9999,
                }}>
                  {/* Header panel */}
                  <div style={{
                    padding: "12px 16px", borderBottom: "1px solid #f3f4f6",
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                  }}>
                    <span style={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>
                      Notificaciones
                    </span>
                    {sinLeer > 0 && (
                      <button onClick={marcarTodasLeidas}
                        style={{ fontSize: 11, color: "#0077B6", background: "none", border: "none", cursor: "pointer", fontWeight: 600, padding: 0 }}>
                        Marcar todas leídas
                      </button>
                    )}
                  </div>

                  {/* Lista */}
                  {notificaciones.length === 0 ? (
                    <div style={{ padding: "30px 16px", textAlign: "center" }}>
                      <div style={{ fontSize: 28, marginBottom: 6 }}>✅</div>
                      <div style={{ fontSize: 13, color: "#9ca3af" }}>No hay citas próximas</div>
                      <div style={{ fontSize: 11, color: "#d1d5db", marginTop: 4 }}>Las citas de hoy y mañana aparecen aquí</div>
                    </div>
                  ) : (
                    notificaciones.map((notif) => {
                      const yaLeida = leidas.includes(notif.id);
                      return (
                        <div
                          key={notif.id}
                          onClick={() => manejarClickNotif(notif)}
                          style={{
                            padding: "12px 16px", cursor: "pointer",
                            borderBottom: "1px solid #f9fafb",
                            background: yaLeida ? "white" : "#faf5ff",
                            borderLeft: yaLeida ? "3px solid transparent" : `3px solid ${notif.color}`,
                            transition: "background 0.15s",
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = "#f0f9ff"}
                          onMouseLeave={(e) => e.currentTarget.style.background = yaLeida ? "white" : "#faf5ff"}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontSize: 16 }}>{notif.emoji}</span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: yaLeida ? 400 : 600, color: "#111827" }}>
                                {notif.titulo}
                              </div>
                              <div style={{ fontSize: 11, color: "#6b7280", marginTop: 1 }}>
                                {notif.detalle}
                              </div>
                              {notif.telefono && (
                                <button onClick={(e) => { e.stopPropagation(); const tel = notif.telefono.replace(/\D/g, ""); const telFull = tel.startsWith("57") ? tel : "57" + tel; window.open(`https://wa.me/${telFull}?text=${encodeURIComponent(`Hola! Te recordamos tu cita ${notif.titulo}. ¡Te esperamos! 🤝`)}`, "_blank"); }}
                                  style={{ marginTop: 4, padding: "2px 8px", borderRadius: 4, border: "1px solid #bbf7d0", background: "#f0fdf4", color: "#16a34a", fontSize: 10, fontWeight: 600, cursor: "pointer" }}>
                                  💬 WhatsApp
                                </button>
                              )}
                            </div>
                            {!yaLeida && (
                              <span style={{ width: 8, height: 8, borderRadius: "50%", background: notif.color, flexShrink: 0 }} />
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}

                  {/* Footer */}
                  <div style={{
                    padding: "10px 16px", borderTop: "1px solid #f3f4f6", textAlign: "center",
                  }}>
                    <button
                      onClick={() => { setMostrarPanel(false); navigate("/agenda"); }}
                      style={{ fontSize: 12, color: "#0077B6", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>
                      Ver agenda completa →
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Cerrar sesión */}
            <button className="sw-navbar-btn" onClick={handleLogout} style={{ marginLeft: 8 }}>
              <span style={{ fontSize: 14 }}>🚪</span>
              <span className="ocultar-movil">Salir</span>
            </button>
          </div>
        )}
      </div>

      <style>{`
        .ocultar-movil { display: inline; }
        @media (max-width: 480px) {
          .ocultar-movil { display: none; }
        }
        @keyframes notifPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.15); }
        }
      `}</style>
    </nav>
  );
};

export default Navbar;